// components/SelectionModal.jsx
'use client';
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  IconButton,
  InputAdornment,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import { useLanguage } from '../utils/i18n';
/**
 * SelectionModal
 * props:
 * - open: bool
 * - onClose: () => void
 * - title: string
 * - options: [{ value, label }]
 * - initialSelected: array of string values (or numbers coerced to string)
 * - onApply: (selectedArray) => void
 * - placeholder: string
 */
export default function SelectionMultiDialog({
  open,
  onClose,
  title = 'Select Items',
  options = [],
  initialSelected = [],
  onApply = () => {},
  placeholder = 'Search...'
}) {
  const normalize = (v) => (v == null ? '' : String(v));
  const [selected, setSelected] = useState(() => (Array.isArray(initialSelected) ? initialSelected.map(normalize) : []));
  const [search, setSearch] = useState('');
  const { t } = useLanguage();

  useEffect(() => {
    setSelected(Array.isArray(initialSelected) ? initialSelected.map(normalize) : []);
  }, [initialSelected, open]);

  const filtered = useMemo(() => {
    const s = String(search || '')
      .trim()
      .toLowerCase();
    if (!s) return (options || []).map((o) => ({ value: normalize(o.value), label: o.label ?? String(o.value) }));
    return (options || [])
      .map((o) => ({ value: normalize(o.value), label: o.label ?? String(o.value) }))
      .filter((o) => o.label.toLowerCase().includes(s) || String(o.value).toLowerCase().includes(s));
  }, [options, search]);

  const toggle = (val) => {
    const v = normalize(val);
    setSelected((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
  };

  const clear = () => setSelected([]);

  const apply = () => {
    // return array of values as strings
    onApply(selected.slice());
    onClose && onClose();
  };

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: '16px' }}>{title}</Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
        </Box>

        <List dense sx={{ maxHeight: 360, overflow: 'auto' }}>
          {filtered.length === 0 && (
            <ListItem>
              <ListItemText primary="No items found" />
            </ListItem>
          )}

          {filtered.map((opt) => {
            const checked = selected.includes(opt.value);
            return (
              <ListItem
                key={opt.value}
                disablePadding
                secondaryAction={<Checkbox edge="end" checked={checked} onChange={() => toggle(opt.value)} />}
              >
                <ListItemButton onClick={() => toggle(opt.value)}>
                  <ListItemText primary={opt.label} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </DialogContent>

      <DialogActions>
        <Box sx={{ flex: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button onClick={clear}>{t('buttonTitle.clear', 'Clear')}</Button>
        </Box>
        <Button onClick={onClose}>{t('buttonTitle.cancel', 'Cancel')}</Button>
        <Button variant="contained" onClick={apply}>
          {t('buttonTitle.apply', 'Apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
